import {
  createClientApmExporterWithTransport,
  createClientErrorTrackingExporterWithTransport,
  ExportResultCode,
  type ClientErrorCreate,
  type CoreHttpExporterOptions,
  type TelExporter,
  type TelExportTransportFactory,
} from '@w/tel'
import { flushTelDb, getOne, putMany } from './idb'

export interface IdbTelExporterOptions extends CoreHttpExporterOptions {
  dbName?: string
  maxSpans?: number
  maxLogs?: number
  maxMetrics?: number
  maxErrorGroups?: number
  maxAlerts?: number
}

export function createIdbExporter(
  opts: IdbTelExporterOptions = {},
): TelExporter {
  const {
    dbName = 'tel',
    maxSpans = 20_000,
    maxLogs = 20_000,
    maxMetrics = 20_000,
    maxErrorGroups = 2_000,
    maxAlerts = 1_000,
    ...core
  } = opts
  const exporterOptions = {
    ...core,
    transport: _transport(dbName, {
      maxSpans,
      maxLogs,
      maxMetrics,
      maxErrorGroups,
      maxAlerts,
    }),
  }
  const errorTracking =
    createClientErrorTrackingExporterWithTransport(exporterOptions)
  const apm = createClientApmExporterWithTransport(exporterOptions)
  return {
    bind(resource, storage) {
      const errorTrackingBound = errorTracking.bind(resource, storage)
      const apmBound = apm.bind(resource, storage)
      return {
        _bindings: [
          ...(errorTrackingBound._bindings ?? [errorTrackingBound]),
          ...(apmBound._bindings ?? [apmBound]),
        ],
      }
    },
  }
}

function _transport(
  dbName: string,
  caps: Pick<
    Required<IdbTelExporterOptions>,
    'maxSpans' | 'maxLogs' | 'maxMetrics' | 'maxErrorGroups' | 'maxAlerts'
  >,
): TelExportTransportFactory {
  return (deploy) => ({
    label: `indexeddb:${dbName}`,
    live: true,
    async sendSpans(spans) {
      await putMany(
        dbName,
        'spans',
        spans.map((span) => ({
          ...span,
          resourceName: deploy.name,
          resourceEnv: deploy.env,
          resourceVersion: deploy.version,
        })),
        caps.maxSpans,
      )
      return { code: ExportResultCode.SUCCESS }
    },
    async sendLogs(logs) {
      await putMany(
        dbName,
        'logs',
        logs.map((log) => ({
          ...log,
          resourceName: deploy.name,
          resourceEnv: deploy.env,
          resourceVersion: deploy.version,
        })),
        caps.maxLogs,
      )
      return { code: ExportResultCode.SUCCESS }
    },
    async sendMetrics(metrics) {
      await putMany(
        dbName,
        'metrics',
        metrics.map((metric) => ({
          ...metric,
          id: `${metric.streamMeta.id}:${metric.collectedAt}`,
          ingestedAt: Date.now(),
          name: metric.streamMeta.name,
          kind: metric.streamMeta.kind,
          temporality:
            'temporality' in metric.streamMeta
              ? metric.streamMeta.temporality
              : undefined,
          resourceName: metric.streamMeta.resource.name,
          resourceEnv: metric.streamMeta.resource.env,
        })),
        caps.maxMetrics,
      )
      return { code: ExportResultCode.SUCCESS }
    },
    async sendErrorCreates(errors) {
      await putMany(
        dbName,
        'errorGroups',
        errors.map((error) => _createErrorGroup(error)),
        caps.maxErrorGroups,
      )
      return { code: ExportResultCode.SUCCESS }
    },
    async sendErrorPatches(patches) {
      for (const patch of patches) {
        const current = await getOne<ErrorGroupValue>(
          dbName,
          'errorGroups',
          patch.groupId,
        )
        if (!current) continue
        await putMany(
          dbName,
          'errorGroups',
          [
            {
              ...current,
              occurrences: current.occurrences + patch.count,
              userIds: _unique(current.userIds, patch.userIds),
              companyIds: _unique(current.companyIds, patch.companyIds),
              lastSeen: patch.occurrence.timestamp,
            },
          ],
          caps.maxErrorGroups,
        )
      }
      return { code: ExportResultCode.SUCCESS }
    },
    loadApmBaselines() {
      return Promise.resolve({ code: ExportResultCode.SUCCESS, baselines: [] })
    },
    async sendApmLatencyAlert(alert) {
      await putMany(dbName, 'alerts', [alert], caps.maxAlerts)
      return { code: ExportResultCode.SUCCESS }
    },
    flush() {
      return flushTelDb(dbName)
    },
  })
}

interface ErrorGroupValue {
  id: string
  occurrences: number
  userIds: string[]
  companyIds: string[]
  lastSeen: number
}
function _createErrorGroup(value: ClientErrorCreate) {
  return {
    ...value.group,
    userIds: value.change.userIds,
    companyIds: value.change.companyIds,
    evidence: value.evidence,
    trace: value.trace,
  }
}
function _unique(left: string[], right: string[]) {
  return [...new Set([...left, ...right])]
}
